import Median from 'median-js-bridge';
import { recordStartupDiagnostic } from './startupDiagnostics';

let listenerId: string | null = null;
let installAttempts = 0;
let retryTimer: number | null = null;
let resumeBindingsInstalled = false;

// Broen kommer typisk inden for få sekunder. Uden et loft kørte denne timer
// videre hvert 250. ms for evigt (også i en almindelig browser), hvilket
// belastede hovedtråden unødigt. Vi genarmer i stedet på focus/visibility.
const MAX_INSTALL_ATTEMPTS = 40; // ~10 sekunder

const scheduleRetry = (): void => {
  if (retryTimer !== null || listenerId !== null) return;
  if (installAttempts >= MAX_INSTALL_ATTEMPTS) return;
  retryTimer = window.setTimeout(() => {
    retryTimer = null;
    installMedianNavigationListener();
  }, 250);
};

const installResumeBindings = (): void => {
  if (resumeBindingsInstalled) return;
  resumeBindingsInstalled = true;
  const rearm = () => {
    // Nyt forsøg efter resume: nulstil tælleren, så loftet ikke låser os ude.
    if (listenerId === null) installAttempts = 0;
    installMedianNavigationListener();
  };
  window.addEventListener('focus', rearm);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') rearm();
  });
};

/**
 * Delivers inbound Median deep links to the already-running React SPA.
 *
 * On a cold start Median loads the complete URL itself. On a warm start it
 * emits jsNavigation instead, so the SPA must consume that event explicitly.
 */
export const installMedianNavigationListener = (): void => {
  installResumeBindings();
  if (listenerId !== null) return;

  // Broen er ikke nødvendigvis klar når modulet indlæses (kold start i Median).
  // Uden dette retry ville lytteren aldrig blive installeret, og et deep link
  // til en kørende app ville efterlade brugeren på den gamle side.
  if (!Median.isNativeApp()) {
    installAttempts += 1;
    // Broen kan blive tilgængelig langt senere efter app-resume. Fortsæt med
    // én begrænset retry-timer og genarm desuden på focus/visibilitychange.
    scheduleRetry();
    return;
  }

  installAttempts = 0;
  listenerId = Median.jsNavigation.url.addListener((event) => {
    const incomingUrl = typeof event === 'string' ? event : event?.url;
    if (!incomingUrl) return;

    try {
      const target = new URL(incomingUrl, window.location.origin);
      const isTaskveoAppLink = target.protocol === 'dk.taskveo.app:' && target.hostname === 'app';
      if (target.origin !== window.location.origin && !isTaskveoAppLink) {
        recordStartupDiagnostic('MEDIAN DEEP LINK AFVIST', 'medianNavigation', {
          reason: 'different_origin',
          origin: target.origin,
        });
        return;
      }

      // Ældre retur-links til forsiden kunne mangle pathname helt. En relativ
      // destination som `?authHandoffSession=...` bevarer den aktuelle route,
      // typisk /reset-password. Normalisér derfor altid tom sti til `/`.
      const normalizedPathname = target.pathname || '/';
      const destination = `${normalizedPathname}${target.search}${target.hash}`;
      recordStartupDiagnostic('MEDIAN DEEP LINK MODTAGET', 'medianNavigation', {
        path: target.pathname,
        queryKeys: Array.from(target.searchParams.keys()),
        hasHash: Boolean(target.hash),
      });

      // BrowserRouter reacts to popstate. The original path, query and hash are
      // forwarded byte-for-byte from Median; no auth parameters are rewritten.
      // Native retur-links har formen dk.taskveo.app://app/<route>. De skal
      // omsættes til en intern BrowserRouter-route; ellers bliver den allerede
      // åbne app stående på sin gamle side (typisk /create-account).
      window.history.replaceState(null, '', destination);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } catch (error) {
      recordStartupDiagnostic('MEDIAN DEEP LINK FEJL', 'medianNavigation', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  });
};